import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import LifeStageLanesSection from '../components/home/LifeStageLanesSection';
import MatchMeCTA from '../components/MatchMeCTA';

/**
 * LifeStageIndexPage
 *
 * Standalone landing at `/where-are-you-at` that re-uses the same
 * LifeStageLanesSection from the homepage. Gives the homepage tile
 * band a canonical URL for indexation, deep-links from email, and a
 * "start here" surface for the mega-menu or any content page.
 *
 * 2026-02-28 warm redesign: hero shares the same #faf7f2 cream as
 * the lanes section below, so the two blocks read as one editorial
 * moment rather than a hero + tile-strip. Serif H1 to match the
 * section headings and pull the whole page toward "home magazine",
 * away from "SaaS marketing site".
 */
const LifeStageIndexPage = () => (
  <div className="min-h-screen bg-white">
    <Helmet>
      <title>Where are you at? | BuildPilot</title>
      <meta
        name="description"
        content="BuildPilot has content, tools and human help for every stage of the home journey, from your first pay-cheque to your third home. Pick where you are today."
      />
      <link rel="canonical" href="https://buildpilot.com.au/where-are-you-at" />
    </Helmet>

    <Header variant="light" />

    <main data-testid="life-stage-index-page">
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 lg:pt-20 pb-2 sm:pb-4">
          <div
            className="inline-flex items-center gap-2 mb-5"
            style={{ color: '#0a2540' }}
          >
            <span className="h-px w-6" style={{ backgroundColor: '#0a2540' }} />
            <span
              className="text-[11px] font-semibold uppercase"
              style={{ letterSpacing: '0.22em' }}
            >
              Start here
            </span>
          </div>
          <h1
            className="text-[32px] sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5"
            style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
          >
            Where are you at?
          </h1>
          <p
            className="text-base sm:text-lg leading-relaxed max-w-2xl"
            style={{ color: '#5a5a5f' }}
          >
            Building or buying a home is a decade-long story, not a one-off transaction. Pick the moment that best describes you today. There is no right or wrong answer, and you can move between lanes any time.
          </p>
        </div>
      </section>
      {/* Batch B: contextual builder-matcher CTA (mid-content) */}
      <div className="max-w-5xl mx-auto px-5 sm:px-6">
        <MatchMeCTA context="hub" />
      </div>

      <LifeStageLanesSection showHeader={false} />
    </main>

    <Footer />
  </div>
);

export default LifeStageIndexPage;
